import request from "../utils/request";

/**
 * Distribution Group 同步配置对应的后端 API 封装。
 * 平台级：/_Admin/UserManagement/DgSyncConfig（AllUser / HongKong 两个范围）
 * Study 级：/_Admin/StudyManagement/DgSyncConfig/{studyId}
 * 后端同步状态为 Pending/Syncing 时表示任务仍在执行，前端据此轮询。
 */

// ────────────── 领域类型 ──────────────

/** 同步任务状态：Pending/Syncing 为进行中，Succeeded/Failed 为终态 */
export type DgSyncStatus = "Pending" | "Syncing" | "Succeeded" | "Failed";

/** 同步范围：0 = AllUser，1 = HongKong，2 = Study */
export type DgSyncScope = 0 | 1 | 2;

/** 前端使用的 DG 同步配置模型（camelCase）。时间为北京时间 "yyyy-MM-dd HH:mm:ss" 字符串 */
export interface DgSyncConfig {
  exists: boolean;
  scope: DgSyncScope;
  studyId: string | null;
  groupEmail: string | null;
  includeNested: boolean;
  userScope: 0 | 1;
  isEnabled: boolean;
  description: string | null;
  lastSavedAt: string | null;
  lastSyncStatus: DgSyncStatus | null;
  lastSyncStartedAt: string | null;
  lastSyncCompletedAt: string | null;
  lastSyncError: string | null;
  syncedUserCount: number | null;
}

/** 保存 DG 同步配置的请求体 */
export interface DgSyncSaveRequest {
  /** 仅保存展示，暂不参与同步过滤；省略时保留已有配置。 */
  userScope?: 0 | 1;
  groupEmail: string;
  includeNested: boolean;
  isEnabled: boolean;
  description: string | null;
}

/** Study 级保存结果：启用和禁用均返回 202；兼容旧接口返回 200 和最新配置。 */
export interface StudyDgSyncSaveResult {
  triggered: boolean;
  config?: DgSyncConfig;
}

// ────────────── Wire 类型（同时容忍 PascalCase / camelCase） ──────────────

interface DgSyncConfigItemWire {
  UserScope?: number;
  userScope?: number;
  Exists?: boolean;
  exists?: boolean;
  Scope?: number;
  scope?: number;
  StudyId?: string | null;
  studyId?: string | null;
  GroupEmail?: string | null;
  groupEmail?: string | null;
  IncludeNested?: boolean;
  includeNested?: boolean;
  IsEnabled?: boolean;
  isEnabled?: boolean;
  Description?: string | null;
  description?: string | null;
  LastSavedAt?: string | null;
  lastSavedAt?: string | null;
  LastSyncStatus?: string | null;
  lastSyncStatus?: string | null;
  LastSyncStartedAt?: string | null;
  lastSyncStartedAt?: string | null;
  LastSyncCompletedAt?: string | null;
  lastSyncCompletedAt?: string | null;
  LastSyncError?: string | null;
  lastSyncError?: string | null;
  SyncedUserCount?: number | null;
  syncedUserCount?: number | null;
}

interface PlatformDgSyncConfigWire {
  AllUser?: DgSyncConfigItemWire;
  allUser?: DgSyncConfigItemWire;
  HongKong?: DgSyncConfigItemWire;
  hongKong?: DgSyncConfigItemWire;
}

interface JobTriggeredWire {
  JobTriggered?: boolean;
  jobTriggered?: boolean;
}

// ────────────── Normalize ──────────────

function normalizeScope(scope: number | undefined): DgSyncScope {
  return scope === 0 || scope === 1 || scope === 2 ? scope : 0;
}

function normalizeSyncStatus(status: string | null | undefined): DgSyncStatus | null {
  return status === "Pending" ||
    status === "Syncing" ||
    status === "Succeeded" ||
    status === "Failed"
    ? status
    : null;
}

function normalizeDgSyncConfig(item: DgSyncConfigItemWire | undefined): DgSyncConfig {
  const wire = item ?? {};
  return {
    exists: wire.Exists ?? wire.exists ?? false,
    scope: normalizeScope(wire.Scope ?? wire.scope),
    studyId: wire.StudyId ?? wire.studyId ?? null,
    groupEmail: wire.GroupEmail ?? wire.groupEmail ?? null,
    includeNested: wire.IncludeNested ?? wire.includeNested ?? false,
    userScope: Number(wire.UserScope ?? wire.userScope) === 1 ? 1 : 0,
    isEnabled: wire.IsEnabled ?? wire.isEnabled ?? false,
    description: wire.Description ?? wire.description ?? null,
    lastSavedAt: wire.LastSavedAt ?? wire.lastSavedAt ?? null,
    lastSyncStatus: normalizeSyncStatus(wire.LastSyncStatus ?? wire.lastSyncStatus),
    lastSyncStartedAt: wire.LastSyncStartedAt ?? wire.lastSyncStartedAt ?? null,
    lastSyncCompletedAt: wire.LastSyncCompletedAt ?? wire.lastSyncCompletedAt ?? null,
    lastSyncError: wire.LastSyncError ?? wire.lastSyncError ?? null,
    syncedUserCount: wire.SyncedUserCount ?? wire.syncedUserCount ?? null,
  };
}

function toWire(payload: DgSyncSaveRequest) {
  return {
    GroupEmail: payload.groupEmail,
    IncludeNested: payload.includeNested,
    ...(payload.userScope !== undefined ? { UserScope: payload.userScope } : {}),
    IsEnabled: payload.isEnabled,
    Description: payload.description,
  };
}

/** 判断同步状态是否为进行中（需要继续轮询） */
export function isDgSyncInProgress(config: DgSyncConfig | null | undefined): boolean {
  return config?.lastSyncStatus === "Pending" || config?.lastSyncStatus === "Syncing";
}

/** 提取后端错误信息（400 等），取值方式与 getStudyErrorMessage 一致 */
export function getDgSyncErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (data && typeof data === "object") {
    const message = (data as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message;
  }
  if (typeof data === "string" && data.trim()) return data;
  return error instanceof Error && error.message ? error.message : fallback;
}

/** 保存接口失败时抛出携带服务端 message 的错误，便于页面直接展示 */
function rethrowWithServerMessage(error: unknown): never {
  throw new Error(getDgSyncErrorMessage(error, error instanceof Error ? error.message : ""));
}

// ────────────── API ──────────────

const USER_BASE = "/_Admin/UserManagement";
const STUDY_BASE = "/_Admin/StudyManagement";

export async function getPlatformDgSyncConfig() {
  const response = await request.get<PlatformDgSyncConfigWire>(`${USER_BASE}/DgSyncConfig`);
  const body = response.data ?? {};
  return {
    allUser: normalizeDgSyncConfig(body.AllUser ?? body.allUser),
    hongKong: normalizeDgSyncConfig(body.HongKong ?? body.hongKong),
  };
}

export async function savePlatformDgSyncConfig(payload: {
  allUser: DgSyncSaveRequest;
  hongKong: DgSyncSaveRequest;
}): Promise<{ triggered: boolean }> {
  try {
    const response = await request.post<JobTriggeredWire>(`${USER_BASE}/DgSyncConfig`, {
      AllUser: toWire(payload.allUser),
      HongKong: toWire(payload.hongKong),
    });
    const triggered = response.data?.JobTriggered ?? response.data?.jobTriggered ?? false;
    if (!triggered)
      throw new Error("Unable to schedule distribution group synchronization. Please retry.");
    return { triggered };
  } catch (error) {
    rethrowWithServerMessage(error);
  }
}

export async function getStudyDgSyncConfig(studyId: string): Promise<DgSyncConfig> {
  const response = await request.get<DgSyncConfigItemWire>(
    `${STUDY_BASE}/DgSyncConfig/${encodeURIComponent(studyId)}`,
  );
  return normalizeDgSyncConfig(response.data);
}

export async function saveStudyDgSyncConfig(
  studyId: string,
  payload: DgSyncSaveRequest,
): Promise<StudyDgSyncSaveResult> {
  try {
    const response = await request.post<JobTriggeredWire & DgSyncConfigItemWire>(
      `${STUDY_BASE}/DgSyncConfig/${encodeURIComponent(studyId)}`,
      toWire(payload),
    );
    const body = response.data ?? {};
    // 202 返回 { jobTriggered }；兼容旧接口以 200 直接返回最新配置。
    const triggered = body.JobTriggered ?? body.jobTriggered;
    if (triggered === false && payload.isEnabled)
      throw new Error("Unable to schedule distribution group synchronization. Please retry.");
    if (triggered !== undefined) return { triggered };
    return { triggered: false, config: normalizeDgSyncConfig(body) };
  } catch (error) {
    rethrowWithServerMessage(error);
  }
}
